// src/rms/pages/Inventory.js
// ============================================================================
// Asset inventory
// ============================================================================
// The add/edit form is generated from the vertical's assetSchema, which is what
// gives a car operator VIN and fuel level while a storage operator gets unit
// size and gate code — from the same component, with no branching.
// ============================================================================

import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import { optionLabel } from "../verticals/presets";
import {
  PageHeader, StatusPill, Modal, Field, TextInput, Select, DynamicField, EmptyState, Banner, DemoPill,
} from "../components/ui";
import adEngine from "../ads/adEngine";
import pinService from "../data/pinService";
import PinModal from "../components/PinModal";
import { getLocale } from "../i18n/locale";

const RATE_UNITS = [
  { value: "hour", label: "Per hour", labelKey: "opt.rateUnit.hour" },
  { value: "half_day", label: "Per half day", labelKey: "opt.rateUnit.halfDay" },
  { value: "day", label: "Per day", labelKey: "opt.rateUnit.day" },
  { value: "week", label: "Per week", labelKey: "opt.rateUnit.week" },
  { value: "month", label: "Per month", labelKey: "opt.rateUnit.month" },
];

const STATUS_OPTIONS = [
  { value: "available", label: "Available", labelKey: "opt.assetStatus.available" },
  { value: "maintenance", label: "Maintenance", labelKey: "opt.assetStatus.maintenance" },
  { value: "out_of_service", label: "Out of service", labelKey: "opt.assetStatus.outOfService" },
  { value: "retired", label: "Retired", labelKey: "opt.assetStatus.retired" },
];

function Inventory() {
  const { operator, config, t, tLower, formatMoney, invalidate, isContinuous, can } = useRms();

  /**
   * An asset waiting for a PIN.
   *
   * Putting a vehicle on the fleet is a purchase, so it takes money rights --
   * owner, manager or bookkeeper, never staff. Unlike the read screens, this
   * one is ENFORCED rather than merely gated: a RESTRICTIVE INSERT policy
   * refuses the row, and a trigger refuses it again without a signature.
   */
  const [pendingAsset, setPendingAsset] = useState(null);
  const { rows: assets } = useCollection("assets", {}, { orderBy: "asset_code" });
  const { rows: groups } = useCollection("assetGroups", {}, { orderBy: "position" });
  const [params, setParams] = useSearchParams();

  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");

  // /rms/inventory?new=1 opens the form directly from the dashboard CTA.
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(blankAsset(config));
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams, config]);

  const groupsById = useMemo(
    () => groups.reduce((acc, g) => ({ ...acc, [g.id]: g }), {}),
    [groups]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (statusFilter && a.status !== statusFilter) return false;
      if (categoryFilter && a.category !== categoryFilter) return false;
      if (publishedFilter === "published" && !a.is_published) return false;
      if (publishedFilter === "unpublished" && a.is_published) return false;
      if (groupFilter === "grouped" && !a.group_id) return false;
      if (groupFilter === "standalone" && a.group_id) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        (a.asset_code || "").toLowerCase().includes(q) ||
        (a.internal_code || "").toLowerCase().includes(q) ||
        (a.serial_number || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    });
  }, [assets, search, statusFilter, categoryFilter, publishedFilter, groupFilter]);

  /** Facet counts, computed over everything so a count never reads as zero
      merely because another facet is already narrowing the list. */
  const facets = useMemo(() => {
    const count = (fn) => assets.filter(fn).length;
    const byCategory = {};
    assets.forEach((a) => {
      if (a.category) byCategory[a.category] = (byCategory[a.category] || 0) + 1;
    });
    const byStatus = {};
    assets.forEach((a) => {
      byStatus[a.status] = (byStatus[a.status] || 0) + 1;
    });
    return {
      byCategory,
      byStatus,
      published: count((a) => a.is_published),
      unpublished: count((a) => !a.is_published),
      grouped: count((a) => a.group_id),
      standalone: count((a) => !a.group_id),
    };
  }, [assets]);

  const togglePublish = async (asset) => {
    adEngine.recordAction();
    await db.update("assets", asset.id, {
      is_published: !asset.is_published,
      published_at: !asset.is_published ? new Date().toISOString() : null,
    });
    invalidate();
  };

  // Columns worth showing at a glance, taken from the vertical's own schema
  // rather than a fixed list.
  const summaryFields = useMemo(
    () => config.assetSchema.filter((f) => f.required).slice(0, 2),
    [config]
  );

  const handleSave = async (draft) => {
    adEngine.recordAction();
    const payload = {
      operator_id: operator.id,
      name: draft.name,
      asset_code: draft.asset_code,
      serial_number: draft.serial_number || null,
      category: draft.category || null,
      status: draft.status,
      base_rate_cents: Math.round(Number(draft.base_rate || 0) * 100),
      rate_unit: draft.rate_unit,
      deposit_cents: Math.round(Number(draft.deposit || 0) * 100),
      custom_fields: draft.custom_fields,
      is_active: true,
    };

    // Editing one is an ordinary write and stays unsigned: a status change or a
    // new photo is the counter's job. Only putting a NEW one on the fleet is
    // signed.
    if (draft.id) {
      await db.update("assets", draft.id, payload);
      setEditing(null);
      invalidate();
      return;
    }
    setPendingAsset(payload);
  };

  const signAndCreate = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "asset.create",
      subjectTable: "rms_assets",
    });
    if (!signed.success) return { error: signed.error };

    // The id handed over here is a PENDING signature. The trigger promotes it
    // to the permanent log in the same transaction as this insert, so a refused
    // write leaves no record claiming the asset was added.
    const created = await db.create("assets", { ...pendingAsset, created_signature_id: signed.data });
    if (!created.success) return { error: created.error };

    setPendingAsset(null);
    setEditing(null);
    invalidate();
    return {};
  };

  return (
    <>
      <PageHeader
        title={t("fleet", "Inventory")}
        subtitle={t("inventory.subtitle", "{count} {items} · {available} available", {
          count: assets.length,
          items: tLower("assetPlural", "assets"),
          available: assets.filter((a) => a.status === "available").length,
        })}
        actions={
          // Hidden from staff. Courtesy: the database refuses the insert too.
          can.addAsset ? (
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankAsset(config, assets.length))}>
              {t("inventory.add", "Add {item}", { item: tLower("assetSingular", "asset") })}
            </button>
          ) : null
        }
      />

      <input
        className="rms-input"
        style={{ maxWidth: 320, marginBottom: 12 }}
        placeholder={t("inventory.search", "Search {items}…", { items: tLower("assetPlural", "assets") })}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Faceted filters with live counts, so staff can see the shape of the
          inventory before they filter it. */}
      <div style={{ display: "flex", gap: 18, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        <FacetGroup
          label={t("inventory.status", "Status")}
          value={statusFilter}
          onChange={setStatusFilter}
          options={Object.entries(facets.byStatus).map(([k, n]) => ({ value: k, label: k.replace(/_/g, " "), count: n }))}
        />
        {Object.keys(facets.byCategory).length ? (
          <FacetGroup
            label={t("inventory.category", "Category")}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={Object.entries(facets.byCategory).map(([k, n]) => ({ value: k, label: k, count: n }))}
          />
        ) : null}
        <FacetGroup
          label={t("inventory.listing", "Listing")}
          value={publishedFilter}
          onChange={setPublishedFilter}
          options={[
            { value: "published", label: "published", count: facets.published },
            { value: "unpublished", label: "unpublished", count: facets.unpublished },
          ]}
        />
        {facets.grouped > 0 ? (
          <FacetGroup
            label={t("inventory.pooling", "Pooling")}
            value={groupFilter}
            onChange={setGroupFilter}
            options={[
              { value: "grouped", label: "in a pool", count: facets.grouped },
              { value: "standalone", label: "standalone", count: facets.standalone },
            ]}
          />
        ) : null}
      </div>

      {groups.length ? (
        <Banner tone="info">
          <div>
            <strong>
              {t("inventory.pooled", "{count, plural, one {# pooled listing.} other {# pooled listings.}}", {
                count: groups.length,
              })}
            </strong>{" "}
            {t("inventory.poolBody", "Customers book the pool and the system assigns a free unit, so one booking no longer blocks the whole listing.")}
          </div>
        </Banner>
      ) : null}

      {assets.length === 0 ? (
        <EmptyState
          title={t("inventory.empty.title", "No {items} yet", { items: tLower("assetPlural", "assets") })}
          message={t("inventory.empty.body", "Add the first thing you rent out and you can start taking {bookings}.", {
            bookings: tLower("reservationPlural", "reservations"),
          })}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankAsset(config))}>
              {t("inventory.add", "Add {item}", { item: tLower("assetSingular", "asset") })}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("assetCode", "Code")}</th>
                <th>{t("assetSingular", "Asset")}</th>
                {summaryFields.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
                <th className="rms-num">{t("tax.rates.rate", "Rate")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th>{t("inventory.listing", "Listing")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12.5 }}>{a.asset_code}</td>
                  <td>
                    <strong>{a.name}</strong>
                    {a.is_demo ? <> <DemoPill /></> : null}
                    <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                      {a.category}
                      {a.group_id && groupsById[a.group_id] ? (
                        <span className="rms-pill rms-pill--info" style={{ marginInlineStart: 6 }}>
                          {groupsById[a.group_id].name}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  {summaryFields.map((f) => (
                    <td key={f.key}>{formatFieldValue(a.custom_fields?.[f.key], f, t)}</td>
                  ))}
                  <td className="rms-num">
                    {formatMoney(a.base_rate_cents)}
                    <div style={{ fontSize: 11.5, color: "var(--rms-muted)" }}>/{a.rate_unit.replace("_", " ")}</div>
                  </td>
                  <td>
                    <StatusPill status={a.status} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="rms-btn rms-btn--ghost rms-btn--sm"
                      onClick={() => togglePublish(a)}
                      title={t("inventory.publishingControlsWhetherCustomers", "Publishing controls whether customers can see and book it — separate from whether it's in service.")}
                    >
                      <StatusPill
                        status={a.is_published ? "active" : "pending"}
                        label={a.is_published ? "published" : "draft"}
                      />
                    </button>
                  </td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toDraft(a))}>
                      {t("action.edit", "Edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <AssetForm
          draft={editing}
          config={config}
          t={t}
          currency={operator.currency}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      ) : null}

      {/* Over the form, not instead of it: cancelling returns to what they
          typed rather than making them fill it in twice. */}
      {pendingAsset ? (
        <PinModal
          title={t("inventory.signAdd", "Who is adding this?")}
          explain={t("inventory.signAddHelp", "Putting one on the fleet is recorded against your PIN.")}
          onCancel={() => setPendingAsset(null)}
          onResolved={signAndCreate}
        />
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------

/** A single facet: clicking the selected value again clears it. */
function FacetGroup({ label, value, onChange, options }) {
  if (!options.length) return null;
  return (
    <div>
      <div className="rms-stat-label" style={{ marginBottom: 5 }}>{label}</div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`rms-btn rms-btn--sm ${value === o.value ? "rms-btn--primary" : "rms-btn--ghost"}`}
            onClick={() => onChange(value === o.value ? "" : o.value)}
          >
            {o.label}
            <span style={{ opacity: 0.65, marginInlineStart: 5 }}>{o.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssetForm({ draft: initial, config, t, currency, onCancel, onSave }) {
  const { tLower } = useRms();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setCustom = (key, value) =>
    setDraft((d) => ({ ...d, custom_fields: { ...d.custom_fields, [key]: value } }));

  const submit = () => {
    if (!draft.name.trim()) return setError(t("inventory.error.name", "Give this {item} a name.", { item: tLower("assetSingular", "asset") }));
    if (!draft.asset_code.trim()) return setError(t("inventory.error.code", "{code} is required — it's how staff refer to it.", {
      code: t("assetCode", "Code"),
    }));

    const missing = config.assetSchema
      .filter((f) => f.required && !hasValue(draft.custom_fields?.[f.key]))
      .map((f) => f.label);
    if (missing.length) return setError(`Required for ${config.label}: ${missing.join(", ")}.`);

    setError(null);
    onSave(draft);
  };

  return (
    <Modal
      title={draft.id ? `Edit ${draft.name}` : t("inventory.form.new", "New {item}", { item: tLower("assetSingular", "asset") })}
      onClose={onCancel}
      width={680}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {draft.id ? t("action.saveChanges", "Save changes") : t("inventory.form.add", "Add {item}", { item: tLower("assetSingular", "asset") })}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-field-grid">
        <Field label={t("inventory.name", "Name")} required>
          <TextInput value={draft.name} onChange={(v) => set("name", v)} placeholder="e.g. 2024 Toyota Corolla" />
        </Field>
        <Field label={t("assetCode", "Code")} required help={t("inventory.shownOnTicketsAnd", "Shown on tickets and used by staff.")}>
          <TextInput value={draft.asset_code} onChange={(v) => set("asset_code", v)} />
        </Field>
        <Field label={t("inventory.category", "Category")}>
          <TextInput value={draft.category} onChange={(v) => set("category", v)} placeholder={t("inventory.optionalGrouping", "Optional grouping")} />
        </Field>
        <Field label={t("inventory.status", "Status")}>
          <Select value={draft.status} onChange={(v) => set("status", v)} options={STATUS_OPTIONS} placeholder={t("inventory.available", "Available")} />
        </Field>
      </div>

      <div style={{ borderTop: "1px solid var(--rms-border)", margin: "6px 0 16px", paddingTop: 16 }}>
        <h3 className="rms-section-title">Pricing ({currency})</h3>
        <div className="rms-field-grid">
          <Field label={t("inventory.baseRate", "Base rate")} required>
            <input
              className="rms-input"
              type="number"
              min="0"
              step="0.01"
              value={draft.base_rate}
              onChange={(e) => set("base_rate", e.target.value)}
            />
          </Field>
          <Field label={t("inventory.charged", "Charged")}>
            <Select value={draft.rate_unit} onChange={(v) => set("rate_unit", v)} options={RATE_UNITS} placeholder={t("inventory.perDay", "Per day")} />
          </Field>
          <Field label={t("deposit", "Deposit")} help={config.workflow.depositRequired ? "Required for this business type." : "Optional."}>
            <input
              className="rms-input"
              type="number"
              min="0"
              step="0.01"
              value={draft.deposit}
              onChange={(e) => set("deposit", e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 16 }}>
        <h3 className="rms-section-title">{config.label} details</h3>
        <div className="rms-field-grid">
          {config.assetSchema.map((field) => (
            <DynamicField
              key={field.key}
              field={field}
              value={draft.custom_fields?.[field.key]}
              allValues={draft.custom_fields || {}}
              onChange={(v) => setCustom(field.key, v)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------------

function blankAsset(config, count = 0) {
  return {
    id: null,
    name: "",
    asset_code: `${config.key.slice(0, 3).toUpperCase()}-${String(count + 1).padStart(3, "0")}`,
    serial_number: "",
    category: "",
    status: "available",
    base_rate: "",
    rate_unit: config.rateDefaults?.primaryUnit || "day",
    deposit: config.rateDefaults?.suggestedDepositCents
      ? (config.rateDefaults.suggestedDepositCents / 100).toString()
      : "",
    custom_fields: {},
  };
}

function toDraft(asset) {
  return {
    id: asset.id,
    name: asset.name,
    asset_code: asset.asset_code,
    serial_number: asset.serial_number || "",
    category: asset.category || "",
    status: asset.status,
    base_rate: ((asset.base_rate_cents || 0) / 100).toString(),
    rate_unit: asset.rate_unit,
    deposit: ((asset.deposit_cents || 0) / 100).toString(),
    custom_fields: asset.custom_fields || {},
  };
}

function hasValue(v) {
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== "";
}

function formatFieldValue(value, field, t) {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length
      ? t("inventory.itemCount", "{count, plural, one {# item} other {# items}}", { count: value.length })
      : "—";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (field.unit && field.unit !== "cents") return `${Number(value).toLocaleString(getLocale())} ${field.unit}`;
  // Option-backed fields store a stable value; show the label that goes with it.
  if (field.options?.length) return optionLabel(field.options, value);
  return String(value);
}

export default Inventory;
